import { ReactNode, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, setAuthTokens, clearAuthTokens, getRefreshToken } from '@/lib/auth';
import { authApi } from '@/lib/api';
import { User } from '@/types';
import toast from 'react-hot-toast';

interface AuthProviderProps {
  children: ReactNode;
}

export default function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await authApi.getProfile();
      if (response.success && response.user) {
        setUser(response.user);
      }
    } catch (error: any) {
      console.error('Auth check failed:', error);
      
      // If we get a 401, it means token is invalid/expired - clear it
      if (error.response?.status === 401) {
        console.log('Token expired or invalid, clearing auth tokens');
        clearAuthTokens();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login({ email, password });
      
      if (response.success && response.user && response.tokens) {
        setAuthTokens(response.tokens);
        setUser(response.user);
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Login failed';
      toast.error(message);
      throw error;
    }
  };

  const register = async (email: string, password: string, name?: string) => {
    try {
      const response = await authApi.register({ email, password, name });
      
      if (response.success && response.user && response.tokens) {
        setAuthTokens(response.tokens);
        setUser(response.user);
        toast.success('Registration successful!');
        navigate('/dashboard');
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Registration error details:', error);
      console.error('Error response:', error.response?.data);
      
      const message = error.response?.data?.message 
        || error.response?.data?.error 
        || error.message 
        || 'Registration failed. Please try again.';
      
      toast.error(message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthTokens();
      setUser(null);
      toast.success('Logged out successfully');
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authApi.getProfile();
      if (response.success && response.user) {
        setUser(response.user);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}